/* eslint-disable prettier/prettier */
import React, { useState, useMemo } from 'react';
import styles from '../../styles/components/controlVariations/DateRangePicker.module.css';

interface DateRangePickerProps {
    onRangeSelect?: (range: { start: Date | null; end: Date | null }) => void;
    markedDates?: Date[];
    minDate?: Date; // Minimum selectable date (defaults to today)
    initialMonth?: Date;
}

const today = new Date();
today.setHours(0, 0, 0, 0);

// Helper functions for date comparison
const isSameDay = (d1: Date | null, d2: Date | null) => {
    if (!d1 || !d2) return false;
    return (
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate()
    );
};

const isBefore = (d1: Date, d2: Date) => {
    return (
        new Date(d1.getFullYear(), d1.getMonth(), d1.getDate()).getTime() <
        new Date(d2.getFullYear(), d2.getMonth(), d2.getDate()).getTime()
    );
};

const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
    onRangeSelect,
    markedDates = [],
    minDate: minDateProp,
    initialMonth = new Date(),
}) => {
    // Minimum selectable date (defaults to today at midnight)
    const effectiveMinDate = useMemo(() => {
        if (minDateProp) {
            const d = new Date(minDateProp);
            d.setHours(0, 0, 0, 0);
            return d;
        }
        return today;
    }, [minDateProp]);

    // Helper to check if a date is before the effective minimum date
    const isPastDate = (date: Date) => {
        const d = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        return d < effectiveMinDate.getTime();
    };

    // Navigation state
    const [currentDate, setCurrentDate] = useState(initialMonth);

    // Selection states
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [hoverDate, setHoverDate] = useState<Date | null>(null);

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // Disable navigation to previous month if we are at/before effectiveMinDate month
    const isPrevDisabled =
        year < effectiveMinDate.getFullYear() ||
        (year === effectiveMinDate.getFullYear() && month <= effectiveMinDate.getMonth());

    // Generate Calendar Grid
    const days = useMemo(() => {
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const firstDayOfWeek = new Date(year, month, 1).getDay();
        const daysInPrevMonth = new Date(year, month, 0).getDate();

        const calendarDays = [];

        // Previous month's trailing days
        for (let i = 0; i < firstDayOfWeek; i++) {
            calendarDays.push({
                date: new Date(
                    year,
                    month - 1,
                    daysInPrevMonth - firstDayOfWeek + i + 1
                ),
                isCurrentMonth: false,
            });
        }

        // Current month's days
        for (let i = 1; i <= daysInMonth; i++) {
            calendarDays.push({
                date: new Date(year, month, i),
                isCurrentMonth: true,
            });
        }

        // Next month's leading days to complete the grid (up to 42 cells)
        const remainingCells = 42 - calendarDays.length;
        for (let i = 1; i <= remainingCells; i++) {
            calendarDays.push({
                date: new Date(year, month + 1, i),
                isCurrentMonth: false,
            });
        }

        return calendarDays;
    }, [year, month]);

    const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setCurrentDate(new Date(year, parseInt(e.target.value), 1));
    };

    const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setCurrentDate(new Date(parseInt(e.target.value), month, 1));
    };

    const changeMonth = (offset: number) => {
        setCurrentDate(new Date(year, month + offset, 1));
    };

    const handleDayClick = (date: Date) => {
        if (isPastDate(date)) return;

        // If no start date OR both dates are already selected (reset range)
        if (!startDate || (startDate && endDate)) {
            setStartDate(date);
            setEndDate(null);
            setHoverDate(null);
            onRangeSelect?.({ start: date, end: null });
        }
        // If start date is selected but no end date
        else if (startDate && !endDate) {
            if (isBefore(date, startDate)) {
                // If user clicks a date before start date, set this as new start date
                setStartDate(date);
                setEndDate(null);
                onRangeSelect?.({ start: date, end: null });
            } else {
                // Set as end date
                setEndDate(date);
                onRangeSelect?.({ start: startDate, end: date });
            }
        }
    };

    const handleDayHover = (date: Date) => {
        if (isPastDate(date)) return;
        if (startDate && !endDate) {
            setHoverDate(date);
        }
    };

    // Determine effective range for visual highlights
    const effectiveStart = startDate;
    const effectiveEnd = endDate || hoverDate;

    const rangeStart =
        effectiveStart && effectiveEnd
            ? isBefore(effectiveStart, effectiveEnd)
                ? effectiveStart
                : effectiveEnd
            : effectiveStart;

    const rangeEnd =
        effectiveStart && effectiveEnd
            ? isBefore(effectiveStart, effectiveEnd)
                ? effectiveEnd
                : effectiveStart
            : effectiveStart;

    const isDateInRange = (date: Date) => {
        if (!rangeStart || !rangeEnd) return false;
        return isBefore(rangeStart, date) && isBefore(date, rangeEnd);
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <button
                    onClick={() => changeMonth(-1)}
                    disabled={isPrevDisabled}
                    className={`${styles.navButton} ${isPrevDisabled ? styles.disabled : ''}`}
                >
                    &lt;
                </button>
                <div className={styles.dropdowns}>
                    <select
                        value={month}
                        onChange={handleMonthChange}
                        className={styles.select}
                    >
                        {MONTHS.map((m, index) => (
                            <option
                                key={m}
                                value={index}
                                disabled={
                                    year === effectiveMinDate.getFullYear() &&
                                    index < effectiveMinDate.getMonth()
                                }
                            >
                                {m}
                            </option>
                        ))}
                    </select>
                    <select
                        value={year}
                        onChange={handleYearChange}
                        className={styles.select}
                    >
                        {Array.from({ length: 10 }).map((_, i) => {
                            const y = effectiveMinDate.getFullYear() + i;
                            return (
                                <option key={y} value={y}>
                                    {y}
                                </option>
                            );
                        })}
                    </select>
                </div>
                <button
                    onClick={() => changeMonth(1)}
                    className={styles.navButton}
                >
                    &gt;
                </button>
            </div>

            <div className={styles.title}>Select Week</div>

            <div className={styles.grid}>
                {days.map((day, idx) => {
                    const isSelectedStart = isSameDay(day.date, rangeStart);
                    const isSelectedEnd = isSameDay(day.date, rangeEnd);
                    const isSelected =
                        isSameDay(day.date, startDate) ||
                        isSameDay(day.date, endDate);
                    const inRange = isDateInRange(day.date);
                    const hasDot = markedDates.some((d) =>
                        isSameDay(d, day.date)
                    );
                    const isSingleSelection = isSameDay(rangeStart, rangeEnd);
                    const isDisabled = isPastDate(day.date);

                    return (
                        <div
                            key={idx}
                            className={`${styles.dayWrapper} ${
                                !day.isCurrentMonth ? styles.notCurrentMonth : ''
                            } ${isSelected ? styles.selected : ''} ${
                                isDisabled ? styles.disabled : ''
                            }`}
                            onClick={() =>
                                !isDisabled && handleDayClick(day.date)
                            }
                            onMouseEnter={() =>
                                !isDisabled && handleDayHover(day.date)
                            }
                        >
                            {/* Render background trail depending on position in range */}
                            {!isSingleSelection && rangeStart && rangeEnd && (
                                <>
                                    {inRange && (
                                        <div
                                            className={`${styles.trail} ${styles.trailFull}`}
                                        />
                                    )}
                                    {isSelectedStart && (
                                        <div
                                            className={`${styles.trail} ${styles.trailStart}`}
                                        />
                                    )}
                                    {isSelectedEnd && (
                                        <div
                                            className={`${styles.trail} ${styles.trailEnd}`}
                                        />
                                    )}
                                </>
                            )}

                            <div className={styles.dayNumber}>
                                {day.date.getDate()}
                            </div>

                            {/* Render the small green indicator dot */}
                            {hasDot && !isSelected && (
                                <div className={styles.dot} />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};